/**
 * Centralized authentication URL configuration
 * 
 * All SSO-related endpoints should use these constants to ensure
 * consistent URL routing through the custom auth domain.
 * 
 * Environment-aware: automatically maps to correct auth worker based on current domain.
 */

/**
 * Get the environment-appropriate auth base URL.
 * Maps login domain to corresponding auth worker:
 * - login.cnvrgnx.com → auth.cnvrgnx.com
 * - login-dev.cnvrgnx.com → auth-dev.cnvrgnx.com
 * - login-preview.cnvrgnx.com → auth-preview.cnvrgnx.com
 */
function getAuthBaseUrl(): string {
  // Allow override via environment variable
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_AUTH_URL) {
    return import.meta.env.VITE_AUTH_URL;
  }
  
  // Server-side or edge function context
  if (typeof window === 'undefined') {
    return 'https://auth.cnvrgnx.com';
  }
  
  const hostname = window.location.hostname;
  
  // Map login domains to auth domains
  if (hostname === 'login-dev.cnvrgnx.com') {
    return 'https://auth-dev.cnvrgnx.com';
  }
  if (hostname === 'login-preview.cnvrgnx.com') {
    return 'https://auth-preview.cnvrgnx.com';
  }
  if (hostname === 'login.cnvrgnx.com') {
    return 'https://auth.cnvrgnx.com';
  }
  
  // Development (localhost, lovable.app) - use dev auth
  if (hostname === 'localhost' || 
      hostname === '127.0.0.1' || 
      hostname.endsWith('.lovable.app') ||
      hostname.endsWith('.lovableproject.com')) {
    return 'https://auth-dev.cnvrgnx.com';
  }
  
  // Default to production
  return 'https://auth.cnvrgnx.com';
}

/**
 * Get the environment-appropriate login URL.
 */
function getLoginUrl(): string {
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_LOGIN_URL) {
    return import.meta.env.VITE_LOGIN_URL;
  }
  
  if (typeof window === 'undefined') {
    return 'https://login.cnvrgnx.com';
  }
  
  const hostname = window.location.hostname;
  
  if (hostname === 'login-dev.cnvrgnx.com' || 
      hostname === 'localhost' || 
      hostname === '127.0.0.1' ||
      hostname.endsWith('.lovable.app') ||
      hostname.endsWith('.lovableproject.com')) {
    return 'https://login-dev.cnvrgnx.com';
  }
  if (hostname === 'login-preview.cnvrgnx.com') {
    return 'https://login-preview.cnvrgnx.com';
  }
  
  return 'https://login.cnvrgnx.com';
}

/**
 * Get the environment-appropriate platform URL.
 * Maps login domain to corresponding platform:
 * - login.cnvrgnx.com → platform.cnvrgnx.com
 * - login-dev.cnvrgnx.com → platform-dev.cnvrgnx.com
 * - login-preview.cnvrgnx.com → platform-preview.cnvrgnx.com
 */
function getPlatformUrl(): string {
  // Allow override via environment variable
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PLATFORM_URL) {
    return import.meta.env.VITE_PLATFORM_URL;
  }
  
  // Server-side context defaults to production
  if (typeof window === 'undefined') {
    return 'https://platform.cnvrgnx.com';
  }
  
  const hostname = window.location.hostname;
  
  // Map login domains to platform domains
  if (hostname === 'login-dev.cnvrgnx.com') {
    return 'https://platform-dev.cnvrgnx.com';
  }
  if (hostname === 'login-preview.cnvrgnx.com') {
    return 'https://platform-preview.cnvrgnx.com';
  }
  if (hostname === 'login.cnvrgnx.com') {
    return 'https://platform.cnvrgnx.com';
  }
  
  // Development (localhost, lovable.app) - use dev platform
  if (hostname === 'localhost' || 
      hostname === '127.0.0.1' || 
      hostname.endsWith('.lovable.app') ||
      hostname.endsWith('.lovableproject.com')) {
    return 'https://platform-dev.cnvrgnx.com';
  }
  
  // Default to production
  return 'https://platform.cnvrgnx.com';
}

/**
 * Get the environment-appropriate SCIM base URL.
 */
function getScimBaseUrl(): string {
  // SCIM is a single instance - no dev/preview variants
  // All environments use production SCIM endpoint
  return 'https://scim.cnvrgnx.com';
}

// Export environment-aware URLs
export const AUTH_BASE_URL = getAuthBaseUrl();
export const LOGIN_URL = getLoginUrl();
export const PLATFORM_URL = getPlatformUrl();
export const MARKETING_URL = 'https://cnvrgnx.com';
export const SCIM_BASE_URL = getScimBaseUrl();

export const AUTH_ENDPOINTS = {
  // SSO/OIDC callback
  SSO_CALLBACK: `${AUTH_BASE_URL}/sso/callback`,
  
  // SCIM provisioning - using dedicated subdomain
  SCIM_BASE: `${SCIM_BASE_URL}/v2`,
  SCIM_OAUTH_TOKEN: `${SCIM_BASE_URL}/oauth/token`,
  
  // SAML endpoints
  SAML_ACS: `${AUTH_BASE_URL}/saml-acs`,
  SAML_SLO: `${AUTH_BASE_URL}/saml-slo`,
  SAML_INITIATE: `${AUTH_BASE_URL}/saml-initiate`,
  SAML_METADATA: (orgId: string) => `${AUTH_BASE_URL}/saml2/metadata?org=${orgId}`,
} as const;
